import type { ScoringProgressRow } from '@gwc/shared';

/**
 * "Glenn 7/8" — who still has a wine to score, so a skipped one is caught
 * before scoring closes (people tap out, or step away, around wine G). Shown to
 * the people running the tasting while scoring is open or just closed. Anyone
 * not finished comes first, with the letters they're missing.
 */
export function ScoringProgress({ rows }: { rows: ScoringProgressRow[] }) {
  if (rows.length === 0) return null;
  const done = rows.filter((r) => r.complete).length;
  const ordered = [...rows].sort(
    (a, b) => Number(a.complete) - Number(b.complete) || a.name.localeCompare(b.name),
  );
  return (
    <div className="card">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm uppercase tracking-wide text-gold-400">Scoring progress</h2>
        <span className="text-xs text-cream-300/60">
          {done} of {rows.length} finished
        </span>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {ordered.map((r) => (
          <li
            key={r.personId}
            className={`flex items-baseline justify-between gap-3 rounded-md border px-3 py-2 ${
              r.complete ? 'border-ink-800 text-cream-300/60' : 'border-gold-600/40 text-cream-50'
            }`}
          >
            <span className="min-w-0">
              <span className="block truncate text-sm">{r.name}</span>
              {!r.complete && r.missing.length > 0 && (
                <span className="block text-xs text-cream-300/60">
                  Missing {r.missing.join(', ')}
                </span>
              )}
            </span>
            <span
              className={`shrink-0 font-display text-lg tabular-nums ${r.complete ? '' : 'text-gold-200'}`}
              aria-label={`${r.scored} of ${r.total} wines scored`}
            >
              {r.scored}/{r.total}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
